/* A library for working with SockJS websockets.
* This is somewhat based on rxjs-websockets but with a lot of customisations
* for freelancer.
*/
import { Inject, Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { Auth } from '@shared/auth';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { async } from 'rxjs/scheduler/async';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { Action } from '../actions';
import { DATASTORE_CONFIG } from '../datastore.config';
import { DatastoreConfig } from '../datastore.interface';
import { RawStoreState } from '../helpers';
import { enterZone, leaveZone } from '../zone-util';
import { MultiSet } from './multiset';

import { SockMessageSend } from './message-send-event.model';
import {
  isSockMessageServerEvent,
  WebsocketMessage,
  WebsocketServerEvent,
} from './server-message-event.model';
import {
  generateChannelSubMessage,
  generateHeader,
  generateOnlineOfflineSub,
  generateOnlineOfflineUnsub,
  Socket,
} from './sock-js';

interface UserSub {
  type: 'sub' | 'unsub';
  id: number;
}

@Injectable()
export class WebSocketService {
  private sock: Socket;
  private connectionStatus = new BehaviorSubject<number>(0);

  public fromServerStream = new Subject<WebsocketServerEvent>();
  private onlineOfflineSubs = new Subject<UserSub>();
  private onlineOfflineSubscription: Subscription;

  private open = false;
  private channelSubscriptions = new Set<number>();
  private onlineOfflineSubscriptions = new MultiSet<number>();
  private unsubDebounceTime?: number; // TODO: T35544

  constructor(
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
    private auth: Auth,
    private store: Store<RawStoreState>,
    private ngZone: NgZone,
  ) {
    this.create();
  }

  private create() {
    this.auth.authState.distinctUntilChanged().subscribe(auth => {
      this.open = false;

      // FIXME: handle logged-out / logging-out
      if (!auth) {
        return;
      }

      const closed = () => {
        if (!this.open) {
          return;
        }

        this.connectionStatus.next(this.connectionStatus.getValue() - 1);
        if (this.onlineOfflineSubscription) {
          this.onlineOfflineSubscription.unsubscribe();
        }
        this.open = false;
      };

      this.sock = new Socket(this.datastoreConfig.webSocketUrl);

      this.sock.onopen = () => {
        this.open = true;
        this.connectionStatus.next(this.connectionStatus.getValue() + 1);
        this.sendEvent(generateHeader(auth));
        this.subscribeToEvents();
      };

      // Only send events with actual messages, not handshaking messages.
      this.sock.onmessage = event => {
        const decodedEvent = JSON.parse(event.data);
        if (isSockMessageServerEvent(decodedEvent)) {
          this.processEvent(decodedEvent, auth.userId);
        }
      };

      this.sock.onclose = (event: CloseEvent) => {
        closed();
        if (event.wasClean) {
          this.fromServerStream.complete();
        } else {
          // FIXME: handle disconnections / retry
          // e.g., window.setTimeout(() => this.create(), 1000);
          // FIXME: use global error toast service to display something when we
          // couln't reconnect.
          console.error(new Error(event.reason));
        }
      };

      return () => {
        if (this.sock) {
          closed();
          this.sock.close();
        }
      };
    });
  }

  private sendEvent(message: SockMessageSend) {
    if (this.open) {
      this.sock.send(message);
    } else {
      console.error('Tried sending a message to a closed websocket.');
    }
  }

  private processEvent(event: WebsocketMessage, toUserId: string) {
    const action: Action = {
      type: 'WS_MESSAGE',
      payload: {
        ...event.body,
        toUserId, // all WebSocket messages are tied to the current user
      },
    };
    this.store.dispatch(action);
    // Once the store is updated then we can let everyone know.
    this.fromServerStream.next(event);
  }

  /* We buffer the user subscriptions and unsubscriptions by 1 second
   * to reduce backend load.
   * It's possible to have subscribed and unsubscriped within that buffer
   * so we remove such sub/unsub (and reverse) manually.
   */
  private subscribeToEvents() {
    // Subscribe to existing subscriptions
    const existingSubscriptions = this.onlineOfflineSubscriptions.values();
    if (existingSubscriptions.length) {
      this.sendEvent(generateOnlineOfflineSub(existingSubscriptions));
    }

    if (this.channelSubscriptions.size) {
      this.sendEvent(
        generateChannelSubMessage(Array.from(this.channelSubscriptions)),
      );
    }

    // Subscribe to any new ones that come in
    this.onlineOfflineSubscription = this.onlineOfflineSubs
      .bufferTime(1000, leaveZone(this.ngZone, async))
      .observeOn(enterZone(this.ngZone, async))
      .subscribe(userIds => {
        const reducedSubscriptions = userIds.reduce(
          (acc: { subs: number[]; unsubs: number[] }, current) => {
            let { subs, unsubs } = acc;

            if (current.type === 'sub') {
              if (acc.unsubs.includes(current.id)) {
                unsubs = acc.unsubs.filter(id => id !== current.id);
              } else {
                subs = [...acc.subs, current.id];
              }
            }

            if (current.type === 'unsub') {
              if (acc.subs.includes(current.id)) {
                subs = acc.subs.filter(id => id !== current.id);
              } else {
                unsubs = [...acc.unsubs, current.id];
              }
            }

            return { subs, unsubs };
          },
          { subs: [], unsubs: [] },
        );

        if (this.open) {
          if (reducedSubscriptions.subs.length) {
            this.sendEvent(generateOnlineOfflineSub(reducedSubscriptions.subs));
          }
          if (reducedSubscriptions.unsubs.length) {
            this.sendEvent(
              generateOnlineOfflineUnsub(reducedSubscriptions.unsubs),
            );
          }
        }
      });
  }

  subscribeOnlineOffline(userId: Observable<number>) {
    // Watch the user id stream, calculate watch for changes,
    // subscribe/unsubscribe to any additions/removals respectively
    // and return the user status for the user who is subscribed to.
    // We add `undefined` to the end so we can get the last unsub.
    return userId
      .distinctUntilChanged()
      .concat(Observable.of(undefined))
      .scan(
        (acc: { previous?: number; current?: number }, next) => ({
          current: next,
          previous: acc.current,
        }),
        {
          current: undefined,
          previous: undefined,
        },
      )
      .map(({ previous, current }) => {
        if (previous) {
          this.unsubscribeUserOnlineOffline(previous);
        }
        if (current) {
          this.subscribeUserOnlineOffline(current);
        }
        return current;
      });
  }

  private subscribeUserOnlineOffline(userId: number) {
    // If this user id is new, subscribe.
    if (this.onlineOfflineSubscriptions.multiplicity(userId) === 0) {
      this.onlineOfflineSubs.next({ type: 'sub', id: userId });
    }

    this.onlineOfflineSubscriptions.add(userId);
  }

  private unsubscribeUserOnlineOffline(userId: number) {
    // If this user id is the last one, unsubscribe.
    if (this.onlineOfflineSubscriptions.multiplicity(userId) === 1) {
      this.onlineOfflineSubs.next({ type: 'unsub', id: userId });
    }

    this.onlineOfflineSubscriptions.remove(userId);
  }

  setChannels(channels: number[]) {
    const currentSubscriptions = Array.from(this.channelSubscriptions);
    const arrayDiff = (a: number[], b: number[]) =>
      a.filter(x => !b.includes(x));

    this.unsubscribeChannels(arrayDiff(currentSubscriptions, channels));
    this.subscribeChannels(arrayDiff(channels, currentSubscriptions));
  }

  private subscribeChannels(channels: number[]) {
    if (channels.length) {
      if (this.open) {
        this.sendEvent(generateChannelSubMessage(channels));
      }
      channels.forEach(channel => this.channelSubscriptions.add(channel));
    }
  }

  private unsubscribeChannels(channels: number[]) {
    if (channels.length) {
      channels.forEach(channel => this.channelSubscriptions.delete(channel));

      /* TODO: Do this properly.
       * Unfortunately the websocket backend doesn't support unsubscribing
       * and so we need to disconnect and reconnect the websocket to unsub :(
       *
       * Ref T35544
       */
      if (this.unsubDebounceTime) {
        clearTimeout(this.unsubDebounceTime);
      }
      this.unsubDebounceTime = window.setTimeout(() => {
        this.unsubDebounceTime = undefined;
        this.sock.close();
        window.setTimeout(() => this.create(), 1000);

        // We should also disconnect the other websocket that libnotify has.
        const w = <any>window;
        if (w.libnotify && w.libnotify.close) {
          w.libnotify.close();
        }
      }, 5000);
    }
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/es/webapp/src/app/@shared/datastore/websocket/websocket.ts